import { REVIEW_ERROR_CODES } from "./contract";
import { apiErrorCode, extractApiError } from "../lib/apiErrors";

/**
 * A reviews refusal, said in the reader's language.
 *
 * Same rule as `admin/affiliate/errorText.js`, for the same reason: the wire carries
 * `detail = {code, message}` and the contract is explicit that the SPA maps the CODE and
 * **never renders `message`** — that string is English and unlocalisable. So:
 *
 *   · a code in `REVIEW_ERROR_CODES` -> `<prefix>.error.<CODE>`, a sentence we have written;
 *   · a code outside it              -> `<prefix>.error.UNKNOWN`. A backend that grows a
 *     refusal this build predates degrades to "we cannot say what went wrong", not to
 *     its own prose;
 *   · NO code at all (a dropped connection, a 500 with an HTML body, pydantic's 422 array)
 *     -> `extractApiError`, the one reader that flattens those shapes safely.
 *
 * `prefix` is the key family of the reader's namespace: `rev` in the `marketplace` and
 * `admin` dictionaries (the client's form and the practitioner's console), `srev` in the
 * operator's `superadmin` dictionary. `tv` is the `common`-bound translator
 * `extractApiError` needs for the keys IT owns; `fallback` is already-resolved text.
 *
 * @param {any} e the axios error
 * @param {{t: function, tv: function, prefix?: "rev"|"srev", fallback?: string}} opts
 * @returns {string}
 */
export function reviewErrorText(e, { t, tv, prefix = "rev", fallback = "" }) {
  const code = apiErrorCode(e);
  if (code) {
    return t(REVIEW_ERROR_CODES.includes(code) ? `${prefix}.error.${code}` : `${prefix}.error.UNKNOWN`);
  }
  return extractApiError(e, fallback, tv);
}

/**
 * The `fields` pointers of a `REVIEW_INVALID` / `REVIEW_PROGRAM_INVALID` envelope, `[]`
 * when the error carries none. Lives beside `reviewErrorText` for the reason
 * `lib/apiErrors.tenantChoices` gives: the envelope is this file's business, and a
 * component reaching into `response.data.detail` itself is exactly the raw-`detail` read
 * `__tests__/apiErrorHygiene.static.test.js` exists to keep out.
 *
 * @param {any} e the axios error
 * @returns {Array<{field: string, rule?: string}>}
 */
export const reviewErrorFields = (e) => {
  const detail = e?.response?.data?.detail;
  return Array.isArray(detail?.fields) ? detail.fields : [];
};

export default reviewErrorText;
